import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { err, type Result, type Route } from '@visious/core';
import {
  fetchClip,
  fetchResolvedTarget,
  type ClipResult,
  type ResolvedTargetResult,
  type ResolveProblem,
} from '../../api/resolve';
import { getPlatform } from '../../platform/instance';
import { navigate } from '../../router/router';
import { resolveRequestFor, resolveStateFor, type ResolveState } from './model';

/**
 * The React binding for §8's two resolving routes. Every decision is in `model.ts`; what is here is
 * the query, and the one effect that turns a resolved route into a navigation.
 *
 * **`staleTime: Infinity`.** A name → UCID mapping is effectively permanent, and a clip's bounds are
 * immutable by construction — so a second visit inside a session must not spend a request, and going
 * Back to the resolver's URL must not re-resolve. That is unlike every other screen in the app, where
 * five minutes is the right answer because the *content* moves.
 *
 * The `enabled` guards are what keep this one hook honest for two endpoints: exactly one of the two
 * requests is ever live, decided by `resolveRequestFor`, so neither endpoint is called speculatively
 * for a route that does not concern it.
 */
export function useResolve(route: Route): ResolveState {
  const request = resolveRequestFor(route);
  const youtubeUrl = request?.youtubeUrl ?? null;
  const clipId = request?.clipId ?? null;

  const name = useQuery({
    queryKey: ['resolveurl', youtubeUrl],
    enabled: youtubeUrl !== null,
    queryFn: async (): Promise<ResolvedTargetResult> => {
      const boot = await getPlatform();
      if (!boot.ok || youtubeUrl === null) return err({ kind: 'bad-url', path: '/api/v1/resolveurl' });
      return await fetchResolvedTarget(boot.platform, youtubeUrl);
    },
    // §3's retry and backoff live in `createApiTransport` (1c).
    retry: false,
    staleTime: Infinity,
  });

  const clip = useQuery({
    queryKey: ['clip', clipId],
    enabled: clipId !== null,
    queryFn: async (): Promise<ClipResult> => {
      const boot = await getPlatform();
      if (!boot.ok || clipId === null) return err({ kind: 'bad-url', path: '/api/v1/clips' });
      return await fetchClip(boot.platform, clipId);
    },
    retry: false,
    staleTime: Infinity,
  });

  const state = resolveStateFor({
    route,
    target: youtubeUrl === null || name.isPending ? null : settled(name.error, name.data),
    clip: clipId === null || clip.isPending ? null : settled(clip.error, clip.data),
  });

  /*
   * **A replace, not a push** (`model.ts` says why: Back would otherwise re-resolve and push forward,
   * which reads as a broken back button). It is in an effect rather than in the render because
   * navigating *is* a side effect — calling it during render would mutate the history stack from a
   * function React is allowed to run twice.
   */
  const href = state.kind === 'ready' ? state.href : null;
  useEffect(() => {
    if (href !== null) navigate(href, { replace: true });
  }, [href]);

  return state;
}

/**
 * A thrown query is a bug or an abort rather than a classified failure — the transport returns its
 * errors as values (`useHashtag.ts` has the same shim). A resolved-but-undefined query cannot happen
 * once `isPending` is false, and reporting it as a network failure is the honest reading of "no answer".
 */
const NO_ANSWER: ResolveProblem = {
  kind: 'transport',
  error: { kind: 'network', status: null, url: '', retryable: true },
};

function settled<T>(
  error: unknown,
  data: Result<T, ResolveProblem> | undefined,
): Result<T, ResolveProblem> {
  if (error !== null && error !== undefined) return err(NO_ANSWER);
  return data ?? err(NO_ANSWER);
}
